import { useCallback, useState } from "react";
import { ApiService } from "../../../api";
import CoverageDocumentService from "../../../api/services/operation/coverage-document-service";
import { translate } from "../../../common/providers";

/**
 * Returns the appropriate title for the side panel based on mode
 * @param {string} mode - Side panel mode
 * @param {Object} selectedDeliverable - Currently selected deliverable
 * @returns {string} Title for the side panel
 */
export const getSidePanelTitle = (mode, selectedDeliverable) => {
	switch (mode) {
		case "import":
			return translate("deliverables-list.import_title");
		case "edit":
			return translate("deliverables-list.edit_deliverable");
		case "view":
			return selectedDeliverable?.ref || "";
		default:
			return translate("deliverables-list.create_deliverable");
	}
};

/**
 * Returns the normalized mode string for the side panel
 * @param {string} mode - Side panel mode
 * @returns {string} Normalized mode
 */
export const getSidePanelMode = (mode) => {
	switch (mode) {
		case "edit":
		case "import":
		case "view":
			return mode;
		default:
			return "add";
	}
};

/**
 * Hook for managing side panel state and deliverable CRUD operations
 * @param {Object} isMountedRef - Ref to track component mount state
 * @param {Function} fetchRows - Function to refresh data
 * @returns {Object} Side panel state and handlers
 */
const useSidePanel = (isMountedRef, fetchRows) => {
	const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
	const [sidePanelMode, setSidePanelMode] = useState("add");
	const [selectedDeliverable, setSelectedDeliverable] = useState(null);

	const openSidePanel = useCallback(({ mode, deliverable = null, loadDialogOptionsCallback = null } = {}) => {
		setSidePanelMode(mode);
		setSelectedDeliverable(deliverable);
		setIsSidePanelOpen(true);
		if (loadDialogOptionsCallback) {
			loadDialogOptionsCallback();
		}
	}, []);

	const handleCloseSidePanel = useCallback(() => {
		setIsSidePanelOpen(false);
	}, []);

	const handleCreateDeliverableSubmit = useCallback(
		async (projectId, payload) => {
			const cancelTokenSource = ApiService.getCancelTokenSource();
			try {
				await CoverageDocumentService.create({ projectId }, payload, cancelTokenSource.token);
				if (isMountedRef?.current) {
					fetchRows();
					setIsSidePanelOpen(false);
				}
			} catch (error) {
				console.error("Error creating deliverable:", error);
			}
		},
		[isMountedRef, fetchRows]
	);

	const handleUpdateDeliverableSubmit = useCallback(
		async (payload) => {
			if (!selectedDeliverable) {
				return;
			}
			const cancelTokenSource = ApiService.getCancelTokenSource();
			try {
				await CoverageDocumentService.update(
					{ docId: selectedDeliverable.id },
					payload,
					cancelTokenSource.token
				);
				if (isMountedRef?.current) {
					fetchRows();
					setIsSidePanelOpen(false);
					setSelectedDeliverable(null);
				}
			} catch (error) {
				console.error("Error updating deliverable:", error);
			}
		},
		[selectedDeliverable, isMountedRef, fetchRows]
	);

	return {
		isSidePanelOpen,
		sidePanelMode,
		selectedDeliverable,
		setSelectedDeliverable,
		openSidePanel,
		handleCloseSidePanel,
		handleCreateDeliverableSubmit,
		handleUpdateDeliverableSubmit,
	};
};

export default useSidePanel;
